"use client";

import { translateProductCategory } from "@/lib/category/catalog-categories";
import { type ReactNode, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { translateResearchLevel } from "@/lib/peptide-translations";
import {
	ArrowDown,
	ArrowUp,
	Check,
	ChevronDown,
	Minus,
	Plus,
	Search,
	SlidersHorizontal,
	X,
} from "lucide-react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuTrigger,
} from "@/ui/components/ui/dropdown-menu";
import { Sheet, SheetCloseButton, SheetContent, SheetHeader, SheetTitle } from "@/ui/components/ui/sheet";
import { cn } from "@/lib/utils";

export type SortOption =
	| "featured"
	| "newest"
	| "price_asc"
	| "price_desc"
	| "rating_desc"
	| "name_asc"
	| "name_desc"
	| "score_desc";

export interface FilterOption {
	name: string;
	count: number;
	hex?: string;
}

export interface CategoryFilterOption {
	id: string;
	name: string;
	slug: string;
	count: number;
}

export interface ActiveFilter {
	key: string;
	label: string;
	value: string;
}

interface FilterBarProps {
	children: ReactNode;
	resultCount: number;
	sortValue: SortOption;
	onSortChange: (value: SortOption) => void;
	activeFilters?: readonly ActiveFilter[];
	onRemoveFilter?: (key: string, value: string) => void;
	onClearFilters?: () => void;
	categoryOptions?: readonly CategoryFilterOption[];
	colorOptions?: readonly FilterOption[];
	sizeOptions?: readonly FilterOption[];
	priceRanges?: readonly { label: string; value: string; count: number }[];
	peptideCategoryOptions?: readonly { name: string; count: number }[];
	researchLevelOptions?: readonly string[];
	availabilityOptions?: readonly string[];
	selectedCategories?: readonly string[];
	selectedColors?: readonly string[];
	selectedSizes?: readonly string[];
	selectedPriceRange?: string | null;
	searchQuery?: string;
	selectedPeptideCategories?: readonly string[];
	selectedResearchLevel?: string;
	selectedAvailability?: string;
	onCategoryToggle?: (slug: string) => void;
	onColorToggle?: (color: string) => void;
	onSizeToggle?: (size: string) => void;
	onPriceRangeChange?: (range: string | null) => void;
	onSearchChange?: (query: string) => void;
	onPeptideCategoryToggle?: (name: string) => void;
	onResearchLevelChange?: (level: string) => void;
	onAvailabilityChange?: (availability: string) => void;
}

const ALL_SECTIONS = [
	"category",
	"price",
	"researchLevel",
	"availability",
	"peptideCategory",
	"color",
	"size",
];

export function FilterBar({
	children,
	resultCount,
	sortValue,
	onSortChange,
	activeFilters = [],
	onRemoveFilter,
	onClearFilters,
	categoryOptions = [],
	colorOptions = [],
	sizeOptions = [],
	priceRanges = [],
	peptideCategoryOptions = [],
	researchLevelOptions = [],
	availabilityOptions = [],
	selectedCategories = [],
	selectedColors = [],
	selectedSizes = [],
	selectedPriceRange = null,
	searchQuery = "",
	selectedPeptideCategories = [],
	selectedResearchLevel = "all",
	selectedAvailability = "all",
	onCategoryToggle,
	onColorToggle,
	onSizeToggle,
	onPriceRangeChange,
	onSearchChange,
	onPeptideCategoryToggle,
	onResearchLevelChange,
	onAvailabilityChange,
}: FilterBarProps) {
	const locale = useLocale();
	const t = useTranslations("plp");
	const availabilityLabel = (value: string) =>
		value === "in_stock"
			? t("availabilityInStock")
			: value === "out_of_stock"
				? t("availabilityOutOfStock")
				: t("allAvailability");
	const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
	const [expandedSections, setExpandedSections] = useState<Set<string>>(() => new Set(ALL_SECTIONS));

	const toggleSection = (key: string) => {
		setExpandedSections((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	};

	const hasFilters =
		categoryOptions.length > 0 ||
		colorOptions.length > 0 ||
		sizeOptions.length > 0 ||
		priceRanges.length > 0 ||
		peptideCategoryOptions.length > 0 ||
		researchLevelOptions.length > 0 ||
		availabilityOptions.length > 0 ||
		!!onSearchChange;

	const activeFilterCount =
		selectedCategories.length +
		selectedColors.length +
		selectedSizes.length +
		(selectedPriceRange ? 1 : 0) +
		(searchQuery.trim() ? 1 : 0) +
		selectedPeptideCategories.length +
		(selectedResearchLevel !== "all" ? 1 : 0) +
		(selectedAvailability !== "all" ? 1 : 0);

	/* ─── Shared filter sections (desktop sidebar + mobile sheet) ─── */
	const filterSections = (
		<div className="divide-border/60 divide-y">
			{/* Category */}
			{categoryOptions.length > 0 && onCategoryToggle && (
				<SidebarSection
					title={t("category")}
					sectionKey="category"
					expandedSections={expandedSections}
					onToggle={toggleSection}
				>
					{categoryOptions.map((cat) => (
						<SidebarCheckbox
							key={cat.slug}
							label={cat.name}
							count={cat.count}
							checked={selectedCategories.includes(cat.slug)}
							onToggle={() => onCategoryToggle(cat.slug)}
						/>
					))}
				</SidebarSection>
			)}

			{/* Price */}
			{priceRanges.length > 0 && onPriceRangeChange && (
				<SidebarSection
					title={t("price")}
					sectionKey="price"
					expandedSections={expandedSections}
					onToggle={toggleSection}
				>
					{priceRanges.map((range) => (
						<SidebarRadio
							key={range.value}
							label={
								range.label.startsWith("Under ")
									? t("priceBelow", { price: range.label.slice(6) })
									: range.label
							}
							checked={selectedPriceRange === range.value}
							onToggle={() => onPriceRangeChange(selectedPriceRange === range.value ? null : range.value)}
						/>
					))}
				</SidebarSection>
			)}

			{/* Research Level */}
			{researchLevelOptions.length > 0 && onResearchLevelChange && (
				<SidebarSection
					title={t("researchLevel")}
					sectionKey="researchLevel"
					expandedSections={expandedSections}
					onToggle={toggleSection}
				>
					{researchLevelOptions.map((level) => (
						<SidebarRadio
							key={level}
							label={translateResearchLevel(level, locale)}
							checked={selectedResearchLevel === level}
							onToggle={() => onResearchLevelChange(selectedResearchLevel === level ? "all" : level)}
						/>
					))}
				</SidebarSection>
			)}

			{/* Availability */}
			{availabilityOptions.length > 0 && onAvailabilityChange && (
				<SidebarSection
					title={t("availability")}
					sectionKey="availability"
					expandedSections={expandedSections}
					onToggle={toggleSection}
				>
					{availabilityOptions.map((a) => (
						<SidebarRadio
							key={a}
							label={availabilityLabel(a)}
							checked={selectedAvailability === a}
							onToggle={() => onAvailabilityChange(selectedAvailability === a ? "all" : a)}
						/>
					))}
				</SidebarSection>
			)}

			{/* Peptide Categories (chip-style) */}
			{peptideCategoryOptions.length > 0 && onPeptideCategoryToggle && (
				<SidebarSection
					title={t("peptideCategories")}
					sectionKey="peptideCategory"
					expandedSections={expandedSections}
					onToggle={toggleSection}
				>
					<div className="flex flex-wrap gap-1.5">
						{peptideCategoryOptions.map((opt) => {
							const isSelected = selectedPeptideCategories.includes(opt.name);
							return (
								<button
									key={opt.name}
									type="button"
									onClick={() => onPeptideCategoryToggle(opt.name)}
									className={cn(
										"py-1.75 border px-2 text-[11px] font-medium transition-all duration-150",
										isSelected
											? "bg-black text-white shadow-sm"
											: "text-muted-foreground hover:bg-muted hover:text-foreground",
									)}
								>
									{translateProductCategory(opt.name, locale)}
									<span className={cn("ml-1 tabular-nums", isSelected ? "opacity-50" : "opacity-40")}>
										{opt.count}
									</span>
								</button>
							);
						})}
					</div>
				</SidebarSection>
			)}

			{/* Color */}
			{colorOptions.length > 0 && onColorToggle && (
				<SidebarSection
					title={t("color")}
					sectionKey="color"
					expandedSections={expandedSections}
					onToggle={toggleSection}
				>
					{colorOptions.map((color) => (
						<SidebarCheckbox
							key={color.name}
							label={color.name}
							count={color.count}
							checked={selectedColors.includes(color.name)}
							onToggle={() => onColorToggle(color.name)}
							swatch={color.hex}
						/>
					))}
				</SidebarSection>
			)}

			{/* Size */}
			{sizeOptions.length > 0 && onSizeToggle && (
				<SidebarSection
					title={t("size")}
					sectionKey="size"
					expandedSections={expandedSections}
					onToggle={toggleSection}
				>
					<div className="flex flex-wrap gap-1.5">
						{sizeOptions.map((size) => {
							const isSelected = selectedSizes.includes(size.name);
							return (
								<button
									key={size.name}
									onClick={() => onSizeToggle(size.name)}
									className={cn(
										"border px-3 py-1 text-[13px] font-medium transition-all duration-150",
										isSelected
											? "border-foreground bg-foreground text-background"
											: "hover:border-foreground/30 border-border text-muted-foreground hover:text-foreground",
									)}
								>
									{size.name}
								</button>
							);
						})}
					</div>
				</SidebarSection>
			)}
		</div>
	);

	return (
		<div className="mx-auto max-w-[1500px] px-4 sm:px-6 lg:px-6">
			{/* ─── Top Bar: Search · Count · Sort ─── */}
			<div className="border-border/60 flex flex-wrap items-center justify-between gap-2.5 border-b py-3.5 sm:flex-nowrap">
				{/* Mobile filter trigger — site-design (matched zu Buttons/Pills) */}
				{hasFilters && (
					<button
						onClick={() => setMobileFiltersOpen(true)}
						className="border-primary-hero/30 hover:bg-primary-hero/[0.04] order-2 inline-flex h-11 items-center gap-2 rounded-full border bg-card px-4 font-mono text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-primary-hero shadow-sm transition-colors hover:border-primary-hero lg:hidden"
					>
						<SlidersHorizontal className="h-3.5 w-3.5" />
						<span>{t("filters")}</span>
						{activeFilterCount > 0 && (
							<span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary-hero px-1 font-mono text-[0.6rem] font-bold tabular-nums text-primary-hero-foreground">
								{activeFilterCount}
							</span>
						)}
					</button>
				)}

				{/* Search — brand-styled pill */}
				{onSearchChange && (
					<div className="relative order-1 w-full sm:order-3 sm:w-auto sm:min-w-[280px] sm:flex-initial">
						<Search className="text-primary-hero/50 pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2" />
						<input
							type="text"
							placeholder={t("searchPlaceholder")}
							value={searchQuery}
							onChange={(e) => onSearchChange(e.target.value)}
							className="placeholder:text-muted-foreground/70 focus:ring-primary-hero/15 h-12 w-full rounded-full border border-border bg-card pl-11 pr-10 font-mono text-[0.75rem] uppercase tracking-[0.12em] text-primary-hero shadow-sm transition-all placeholder:font-mono placeholder:text-[0.7rem] placeholder:uppercase placeholder:tracking-[0.18em] focus:border-primary-hero focus:outline-none focus:ring-2"
						/>
						{searchQuery && (
							<button
								type="button"
								onClick={() => onSearchChange("")}
								aria-label={t("clearSearch")}
								className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-primary-hero hover:text-primary-hero-foreground"
							>
								<X className="h-3.5 w-3.5" />
							</button>
						)}
					</div>
				)}

				<div className="order-4 hidden flex-1 sm:block" />

				{/* Result count */}
				<span className="order-4 hidden text-sm tabular-nums text-muted-foreground sm:block">
					{t("resultCount", { count: resultCount })}
				</span>

				{/* Sort */}
				<DropdownMenu>
					<DropdownMenuTrigger className="order-4 inline-flex h-12 shrink-0 items-center gap-2 rounded-full bg-primary-hero px-5 font-mono text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-primary-hero-foreground shadow-md transition-colors hover:bg-primary data-[state=open]:bg-primary">
						{t("sort")}
						<ChevronDown className="h-3.5 w-3.5 opacity-40" />
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="w-52">
						<DropdownMenuRadioGroup value={sortValue} onValueChange={(v) => onSortChange(v as SortOption)}>
							<DropdownMenuRadioItem value="featured">{t("sortFeatured")}</DropdownMenuRadioItem>
							<DropdownMenuRadioItem value="newest">{t("sortNewest")}</DropdownMenuRadioItem>
							<DropdownMenuRadioItem value="name_asc">{t("sortNameAsc")}</DropdownMenuRadioItem>
							<DropdownMenuRadioItem value="name_desc">{t("sortNameDesc")}</DropdownMenuRadioItem>
							<DropdownMenuRadioItem value="price_asc" className="flex items-center gap-1.5">
								<ArrowUp className="h-3 w-3 shrink-0" aria-hidden />
								{t("sortPriceAsc")}
							</DropdownMenuRadioItem>
							<DropdownMenuRadioItem value="price_desc" className="flex items-center gap-1.5">
								<ArrowDown className="h-3 w-3 shrink-0" aria-hidden />
								{t("sortPriceDesc")}
							</DropdownMenuRadioItem>
							<DropdownMenuRadioItem value="score_desc">{t("sortScoreDesc")}</DropdownMenuRadioItem>
							<DropdownMenuRadioItem value="rating_desc">{t("sortRatingDesc")}</DropdownMenuRadioItem>
						</DropdownMenuRadioGroup>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			{/* ─── Active Filter Pills ─── */}
			{activeFilters.length > 0 && onRemoveFilter && onClearFilters && (
				<div className="border-border/40 flex flex-wrap items-center gap-1.5 border-b py-2.5">
					{activeFilters.map((filter) => {
						const displayValue =
							filter.key === "researchLevel"
								? translateResearchLevel(filter.value, locale)
								: filter.key === "availability"
									? availabilityLabel(filter.value)
									: filter.key === "peptideCategory"
										? translateProductCategory(filter.value, locale)
										: filter.value;
						return (
							<span
								key={`${filter.key}-${filter.value}`}
								className="bg-primary/8 inline-flex shrink-0 items-center gap-1 rounded-md py-1 pl-2.5 pr-1 text-[12px] font-medium text-primary"
							>
								{displayValue}
								<button
									onClick={() => onRemoveFilter(filter.key, filter.value)}
									className="hover:bg-primary/15 rounded p-0.5 transition-colors"
								>
									<X className="h-3 w-3" />
									<span className="sr-only">{t("removeFilter", { value: displayValue })}</span>
								</button>
							</span>
						);
					})}
					<button
						onClick={onClearFilters}
						className="shrink-0 rounded px-2 py-0.5 text-[12px] text-muted-foreground transition-colors hover:text-foreground"
					>
						{t("clearAll")}
					</button>
				</div>
			)}

			{/* ─── Content: Sidebar + Grid ─── */}
			<div className="flex gap-8 pt-6 lg:gap-10">
				{/* Desktop Sidebar — Eurolab framed-widget treatment with blueprint raster */}
				{hasFilters && (
					<aside className="hidden w-[280px] shrink-0 lg:block">
						<div className="sticky top-32 max-h-[calc(100vh-9rem)] overflow-hidden">
							<div className="relative overflow-hidden rounded-2xl border border-border bg-background shadow-[0_30px_60px_-40px_rgba(8,39,74,0.18)]">
								{/* Blueprint raster */}
								<div
									aria-hidden
									className="pointer-events-none absolute inset-0 opacity-[0.035]"
									style={{
										backgroundImage:
											"linear-gradient(to right, var(--primary) 1px, transparent 1px), linear-gradient(to bottom, var(--primary) 1px, transparent 1px)",
										backgroundSize: "28px 28px",
									}}
								/>
								{/* Top crosshairs */}
								<svg
									aria-hidden
									viewBox="0 0 12 12"
									className="text-primary/30 pointer-events-none absolute left-2 top-2 h-3 w-3"
									fill="none"
									stroke="currentColor"
									strokeWidth="1"
								>
									<path d="M0 4 V0 H4" />
								</svg>
								<svg
									aria-hidden
									viewBox="0 0 12 12"
									className="text-primary/30 pointer-events-none absolute right-2 top-2 h-3 w-3 rotate-90"
									fill="none"
									stroke="currentColor"
									strokeWidth="1"
								>
									<path d="M0 4 V0 H4" />
								</svg>

								{/* Eyebrow header */}
								<div className="border-border/80 relative flex items-center justify-between border-b px-5 pb-3 pt-4">
									<div className="flex items-center gap-2 font-mono text-[0.58rem] uppercase tracking-[0.22em] text-primary">
										<span className="relative flex h-1.5 w-1.5">
											<span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-success" />
										</span>
										{t("filters")}
									</div>
									{activeFilterCount > 0 && (
										<span className="font-mono text-[0.55rem] uppercase tabular-nums tracking-[0.18em] text-primary">
											{t("activeFilterCount", { count: activeFilterCount })}
										</span>
									)}
								</div>

								<nav className="relative max-h-[calc(100vh-13rem)] overflow-y-auto overscroll-contain px-4 pb-6 pt-4 [-ms-overflow-style:none] [scrollbar-width:thin]">
									{filterSections}
								</nav>
							</div>
						</div>
					</aside>
				)}

				{/* Mobile Filter Sheet — Site-Design */}
				<Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
					<SheetContent side="left" className="flex w-[320px] flex-col p-0 sm:w-[360px]">
						<SheetHeader className="flex-col items-start gap-1.5 border-b border-border bg-card px-5 py-5">
							<div className="flex w-full items-center justify-between">
								<div className="flex items-center gap-2 font-mono text-[0.58rem] font-semibold uppercase tracking-[0.22em] text-primary-hero">
									<span aria-hidden className="bg-primary-hero/60 block h-px w-6" />
									{t("filters")}
								</div>
								<SheetCloseButton />
							</div>
							<SheetTitle className="font-display text-[1.3rem] font-medium tracking-[-0.018em]">
								{t("filters")}
							</SheetTitle>
						</SheetHeader>

						<div className="flex-1 overflow-y-auto overscroll-contain px-5 py-3">{filterSections}</div>

						<div className="border-t border-border bg-background p-4">
							<button
								onClick={() => setMobileFiltersOpen(false)}
								className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-primary-hero font-mono text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-primary-hero-foreground shadow-md transition-colors hover:bg-primary"
							>
								{t("showResults", { count: resultCount })}
							</button>
							{activeFilterCount > 0 && onClearFilters && (
								<button
									onClick={() => {
										onClearFilters();
										setMobileFiltersOpen(false);
									}}
									className="mt-2 w-full font-mono text-[0.62rem] uppercase tracking-[0.16em] text-muted-foreground transition-colors hover:text-foreground"
								>
									{t("clearAllFilters", { count: activeFilterCount })}
								</button>
							)}
						</div>
					</SheetContent>
				</Sheet>

				{/* Main Content */}
				<div className="min-w-0 flex-1 pb-8">{children}</div>
			</div>
		</div>
	);
}

/* ─────────────────────────────────────────────────
 * Sidebar sub-components
 * ───────────────────────────────────────────────── */

function SidebarSection({
	title,
	sectionKey,
	expandedSections,
	onToggle,
	children,
}: {
	title: string;
	sectionKey: string;
	expandedSections: Set<string>;
	onToggle: (key: string) => void;
	children: ReactNode;
}) {
	const isOpen = expandedSections.has(sectionKey);

	return (
		<div className="border-b border-border py-4 first:pt-1 last:border-b-0">
			<button
				onClick={() => onToggle(sectionKey)}
				className="flex w-full items-center justify-between font-mono text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-foreground transition-colors hover:text-primary-hero"
			>
				{title}
				{isOpen ? (
					<Minus className="h-3.5 w-3.5 text-primary-hero" />
				) : (
					<Plus className="text-primary-hero/60 h-3.5 w-3.5" />
				)}
			</button>
			<div
				className={cn(
					"grid transition-[grid-template-rows] duration-200 ease-out",
					isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
				)}
			>
				<div className="overflow-hidden">
					<div className="mt-3 space-y-0.5">{children}</div>
				</div>
			</div>
		</div>
	);
}

function SidebarCheckbox({
	label,
	count,
	checked,
	onToggle,
	swatch,
}: {
	label: string;
	count?: number;
	checked: boolean;
	onToggle: () => void;
	swatch?: string;
}) {
	return (
		<button
			onClick={onToggle}
			className="hover:bg-muted/60 group flex w-full items-center gap-2.5 rounded-md px-1.5 py-[5px] text-left transition-colors"
		>
			<span
				className={cn(
					"flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-[3px] border transition-colors duration-150",
					checked
						? "border-foreground bg-foreground text-background"
						: "border-border/80 group-hover:border-foreground/30",
				)}
			>
				{checked && <Check className="h-3 w-3" strokeWidth={2.5} />}
			</span>
			{swatch && (
				<span
					className={cn(
						"h-3.5 w-3.5 shrink-0 rounded-full border transition-shadow",
						checked ? "border-foreground/40 ring-foreground/10 ring-2" : "border-border/60",
					)}
					style={{ backgroundColor: swatch }}
				/>
			)}
			<span className="text-foreground/75 group-hover:text-foreground flex-1 text-[13px] transition-colors">
				{label}
			</span>
			{count != null && <span className="text-muted-foreground/60 text-[11px] tabular-nums">{count}</span>}
		</button>
	);
}

function SidebarRadio({
	label,
	checked,
	onToggle,
}: {
	label: string;
	checked: boolean;
	onToggle: () => void;
}) {
	return (
		<button
			onClick={onToggle}
			className="hover:bg-muted/60 group flex w-full items-center gap-2.5 rounded-md px-1.5 py-[5px] text-left transition-colors"
		>
			<span
				className={cn(
					"flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-full border transition-colors duration-150",
					checked ? "border-foreground bg-foreground" : "border-border/80 group-hover:border-foreground/30",
				)}
			>
				{checked && <span className="h-1.5 w-1.5 rounded-full bg-background" />}
			</span>
			<span className="text-foreground/75 group-hover:text-foreground flex-1 text-[13px] transition-colors">
				{label}
			</span>
		</button>
	);
}
